import { Logger } from '../../../logger/src/Logger';
import {
  emitPresenceFirstUpdateLatency,
  emitPresenceSubscriptionRequest,
  recordPresenceStatusUpdate,
  resolvePresenceSurface,
  teardownPresenceMetrics,
} from '../utils/presenceMetrics';
import { convertPresenceType, PresenceData, PresenceType } from '../utils/presenceUtils';
import { resolveWebexUserId } from '../utils/webexUserIdMapper';
import { whenDeviceRegistered, whenWebexReady } from './WebexInstanceService';

/**
 * UI presence data with converted types
 */
export interface UIPresenceData {
  presenceType: PresenceType;
  presenceLabel: string;
  customStatusMessage?: string;
}

/**
 * Callback function type for presence updates
 */
export type PresenceUpdateCallback = (presenceMap: Map<string, PresenceData>) => void;

/**
 * Centralized presence manager — Webex JS SDK presence.
 *
 * - `enqueue`/`dequeue`: worker queue (SDK may delay realtime subscription ~60s after enqueue alone).
 * - `subscribe`/`unsubscribe`: immediate subscription path used together with enqueue so UI updates in realtime.
 * - Init: device registered → **`on('updated')` before worker start** → `enable()` if needed → enqueue/subscribe from features.
 * - Worker: `@webex/internal-plugin-presence` 2.x has **no** `initializeWorker()`; realtime requires **`presence.worker.initialize(webex)`** (Mercury `apheleia.subscription_update` → `updated`).
 * - Use `PresenceManager.obtain(webex)` for a single shared instance (see PresenceProvider).
 */
export class PresenceManager {
  /** Single shared manager for the app; cleared in `destroy()`. */
  private static shared: PresenceManager | null = null;

  /**
   * Returns the existing non-destroyed instance or creates one. Prefer over `new` in production.
   */
  static obtain(webex: any): PresenceManager {
    if (PresenceManager.shared && !PresenceManager.shared.isDestroyed) {
      PresenceManager.shared.rebindWebex(webex);
      Logger.info('[PresenceManager] obtain() — reusing shared instance');
      return PresenceManager.shared;
    }
    PresenceManager.shared = new PresenceManager(webex);
    Logger.info('[PresenceManager] obtain() — created shared instance');
    return PresenceManager.shared;
  }

  private featureSubscriptions: Map<string, Set<string>> = new Map();
  private userRefCount: Map<string, number> = new Map();
  private presenceMap: Map<string, PresenceData> = new Map();

  private webex: any;
  private isInitialized: boolean = false;
  private updateHandler: ((envelope: any) => void) | null = null;
  private updateCallbacks: Set<PresenceUpdateCallback> = new Set();
  private initializationPromise: Promise<void> | null = null;

  /** Serialize register/unregister to avoid overlapping enqueue/dequeue ordering issues */
  private registrationQueue = Promise.resolve();

  /** performance.now() when user ref first became 1 — for first-update latency metric */
  private pendingFirstPresenceMs: Map<string, number> = new Map();

  private notifyScheduled = false;
  /** Ensures presence worker / Mercury apheleia listener runs at most once for this manager */
  private presenceWorkerStarted = false;
  /** Timeouts for delayed presence.list fallback after enqueue */
  private listFallbackTimers: ReturnType<typeof setTimeout>[] = [];
  /** 5s re-enqueue fallback timers */
  private reenqueueFallbackTimers: ReturnType<typeof setTimeout>[] = [];
  /** Warn if `updated` never fired shortly after presence init */
  private noUpdatedWarningTimer: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic marker: last time `presence.on('updated')` ran (for fallback diagnostics) */
  private lastUpdatedEventTime = 0;
  /** Set true after destroy(); used by `obtain()` */
  isDestroyed = false;

  private static readonly PERSON_UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  /** TTL (seconds) for `presence.subscribe()` — matches common Webex SDK usage */
  private static readonly SUBSCRIBE_TTL_SEC = 600;

  /** Apheleia payload field refining `meeting` status (e.g. `calendarItem`). */
  private static pickMeetingType(source: any): string | undefined {
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    const mt = source.meetingType;
    return typeof mt === 'string' && mt.trim().length > 0 ? mt : undefined;
  }

  constructor(webex: any) {
    this.webex = webex;
  }

  private rebindWebex(webex: any): void {
    if (webex) {
      this.webex = webex;
    }
  }

  /** One-time: ensure presence module is enabled (SDK may skip push until enabled). */
  private async ensurePresenceEnabled(presence: any): Promise<void> {
    try {
      if (typeof presence.isEnabled === 'function') {
        const enabled = await presence.isEnabled();
        if (enabled === false && typeof presence.enable === 'function') {
          Logger.info('[PresenceManager] presence not enabled — calling enable()');
          await presence.enable();
        } else {
          Logger.info(`[PresenceManager] presence isEnabled=${String(enabled)}`);
        }
      } else if (typeof presence.enable === 'function') {
        await presence.enable();
        Logger.info('[PresenceManager] presence.enable() called (no isEnabled)');
      }
    } catch (e) {
      Logger.info(
        `[PresenceManager] presence enable/isEnabled skipped: ${e instanceof Error ? e.message : e}`
      );
    }
  }

  /**
   * Start the SDK presence worker so Mercury forwards `event:apheleia.subscription_update` to `presence.on('updated')`.
   * Newer SDKs may expose `presence.initializeWorker()`; 2.x only uses `presence.worker.initialize(webex)` behind `config.initializeWorker`.
   */
  private startPresenceWorker(presence: any): void {
    if (this.presenceWorkerStarted) {
      return;
    }

    if (typeof presence.initializeWorker === 'function') {
      try {
        presence.initializeWorker();
        this.presenceWorkerStarted = true;
        Logger.info('[PresenceManager] presence.initializeWorker()');
      } catch (error) {
        Logger.error(
          `[PresenceManager] initializeWorker failed: ${error instanceof Error ? error.message : error}`
        );
      }
      return;
    }

    const worker = presence.worker;
    if (worker && typeof worker.initialize === 'function') {
      if (worker.webex && worker.webex === this.webex) {
        this.presenceWorkerStarted = true;
        Logger.info('[PresenceManager] presence.worker already initialized (SDK)');
        return;
      }
      try {
        worker.initialize(this.webex);
        this.presenceWorkerStarted = true;
        Logger.info(
          '[PresenceManager] presence.worker.initialize(webex) — apheleia Mercury → presence.on("updated")'
        );
      } catch (error) {
        Logger.error(
          `[PresenceManager] presence.worker.initialize failed: ${error instanceof Error ? error.message : error}`
        );
      }
      return;
    }

    Logger.info('[PresenceManager] No presence worker API found — realtime updates may never arrive');
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    this.initializationPromise = this._doInitialize();
    return this.initializationPromise;
  }

  private async _doInitialize(): Promise<void> {
    if (this.isDestroyed) {
      return;
    }
    try {
      if (!this.webex) {
        await whenWebexReady(20000);
        this.webex = (window as any).webex;
      }

      try {
        await whenDeviceRegistered(20000);
        Logger.info('[PresenceManager] Device registered — initializing presence');
      } catch (error) {
        Logger.error(
          `[PresenceManager] Device not registered before presence init: ${error instanceof Error ? error.message : error}`
        );
        return;
      }

      if (!this.webex?.internal?.presence) {
        Logger.info('[PresenceManager] Webex presence module not available');
        return;
      }

      const presence = this.webex.internal.presence;

      await this.ensurePresenceEnabled(presence);

      const updatedListenerSince = Date.now();
      if (typeof presence.on === 'function') {
        this.updateHandler = this.handleUpdate.bind(this);
        presence.on('updated', this.updateHandler);
        Logger.info('[PresenceManager] Subscribed presence.on("updated") BEFORE worker start (SDK recommendation)');
      } else {
        Logger.error('[PresenceManager] presence.on() not available');
      }

      this.startPresenceWorker(presence);

      this.reenqueueAllSubscribedPersonIds('after worker + listener (forced re-subscribe)');

      this.isInitialized = true;
      Logger.info('[PresenceManager] Initialization complete (enable → on(updated) → worker → re-enqueue)');

      if (this.noUpdatedWarningTimer) {
        clearTimeout(this.noUpdatedWarningTimer);
      }
      this.noUpdatedWarningTimer = setTimeout(() => {
        this.noUpdatedWarningTimer = null;
        if (this.isDestroyed) {
          return;
        }
        if (this.lastUpdatedEventTime < updatedListenerSince) {
          Logger.info(
            '[PresenceManager] DIAG: presence.on("updated") never fired within 10s after listener registration — check Mercury / person UUIDs / SDK'
          );
        }
      }, 10000);
    } catch (error) {
      Logger.error(`[PresenceManager] Initialization failed: ${error instanceof Error ? error.message : error}`);
      throw error;
    }
  }

  async registerFeature(featureId: string, userIds: string[]): Promise<void> {
    this.registrationQueue = this.registrationQueue.then(() => this.processRegisterFeature(featureId, userIds));
    return this.registrationQueue;
  }

  private async processRegisterFeature(featureId: string, userIds: string[]): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.webex?.internal?.presence) {
      Logger.info(`[PresenceManager] Cannot register feature ${featureId}: presence unavailable`);
      return;
    }

    const validUserIds = this.validateUserIds(userIds);
    if (validUserIds.length !== userIds.length) {
      Logger.info(
        `[PresenceManager] registerFeature(${featureId}): dropped ${userIds.length - validUserIds.length} empty/invalid id(s)`
      );
    }

    const canonicalUserIds = await this.resolveUserIdsForPresence(validUserIds);
    if (canonicalUserIds.length < validUserIds.length) {
      Logger.info(
        `[PresenceManager] registerFeature(${featureId}): could not resolve ${validUserIds.length - canonicalUserIds.length} id(s) to person UUID`
      );
    }

    const previous = this.featureSubscriptions.get(featureId) || new Set<string>();
    const current = new Set(canonicalUserIds);

    const usersToRemove: string[] = [];
    previous.forEach((userId) => {
      if (!current.has(userId)) {
        usersToRemove.push(userId);
      }
    });

    const usersToAdd: string[] = [];
    canonicalUserIds.forEach((userId) => {
      if (!previous.has(userId)) {
        usersToAdd.push(userId);
      }
    });

    if (usersToRemove.length > 0) {
      this.applyUsersRemoved(usersToRemove);
    }
    const batchStartMs = Date.now();
    if (usersToAdd.length > 0) {
      this.applyUsersAdded(usersToAdd);
      await this.subscribePersonIdsImmediate(usersToAdd);
      this.fetchPresenceListBatch(usersToAdd);
      this.scheduleListFallbackForUsers(usersToAdd, batchStartMs);
      this.scheduleReenqueueFallback(batchStartMs);
      this.reenqueueAllSubscribedPersonIds('after feature add');
    }

    this.featureSubscriptions.set(featureId, current);

    Logger.info(
      `[PresenceManager] registerFeature(${featureId}): ${canonicalUserIds.length} canonical user(s) (${usersToAdd.length} added, ${usersToRemove.length} removed)`
    );

    if (usersToAdd.length > 0) {
      try {
        emitPresenceSubscriptionRequest(featureId, usersToAdd.length);
      } catch (e) {
        Logger.error(
          `[PresenceManager] emitPresenceSubscriptionRequest failed: ${e instanceof Error ? e.message : String(e)}`
        );
      }
    }
  }

  async unregisterFeature(featureId: string): Promise<void> {
    const userIds = this.featureSubscriptions.get(featureId);
    if (!userIds || userIds.size === 0) {
      this.featureSubscriptions.delete(featureId);
      return;
    }

    const userIdsArray = Array.from(userIds);
    this.applyUsersRemoved(userIdsArray);
    this.featureSubscriptions.delete(featureId);
    this.cleanupUnusedPresence();

    Logger.info(`[PresenceManager] Unregistered feature ${featureId}: ${userIdsArray.length} users`);
  }

  getPresence(userId: string): UIPresenceData | undefined {
    if (!userId) {
      return undefined;
    }
    const presenceData = this.presenceMap.get(userId);
    if (!presenceData) {
      return undefined;
    }
    const { presenceType, presenceLabel } = convertPresenceType(
      presenceData.status,
      presenceData.category,
      presenceData.meetingType
    );
    return {
      presenceType,
      presenceLabel,
      customStatusMessage: presenceData.customStatusMessage,
    };
  }

  getRawPresence(userId: string): PresenceData | undefined {
    return this.presenceMap.get(userId);
  }

  /**
   * Single-user snapshot (e.g. contact card). Does not drive main list flow — events do.
   */
  async fetchPresenceImmediately(userId: string): Promise<UIPresenceData | undefined> {
    if (!userId || !this.webex?.internal?.presence) {
      return undefined;
    }

    const existing = this.getPresence(userId);
    if (existing) {
      return existing;
    }

    const presence = this.webex.internal.presence;

    try {
      if (typeof presence.get === 'function') {
        const result = await presence.get(userId);
        if (result) {
          const resultUserId = result?.subject || result?.userId || result?.id || userId;
          this.updatePresenceData(resultUserId, {
            status: (result.status || 'unknown') as any,
            category: result.category || null,
            customStatusMessage: result.customStatusMessage,
            meetingType: PresenceManager.pickMeetingType(result),
          });
          return this.getPresence(resultUserId);
        }
      }
      if (typeof presence.list === 'function') {
        const body = await presence.list([userId]);
        const item = body?.statusList?.[0];
        if (item) {
          const id = item?.subject || item?.userId || item?.id || userId;
          this.updatePresenceData(id, {
            status: (item.status || 'unknown') as any,
            category: item.category || null,
            customStatusMessage: item.customStatusMessage,
            meetingType: PresenceManager.pickMeetingType(item),
          });
          return this.getPresence(id);
        }
      }
    } catch (error) {
      Logger.error(`[PresenceManager] Immediate fetch failed for ${userId}: ${error instanceof Error ? error.message : error}`);
    }

    return undefined;
  }

  getAllPresence(): Map<string, PresenceData> {
    return new Map(this.presenceMap);
  }

  subscribe(callback: PresenceUpdateCallback): () => void {
    if (this.isDestroyed) {
      return () => {};
    }
    this.updateCallbacks.add(callback);
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  destroy(): void {
    if (this.isDestroyed) {
      return;
    }
    this.isDestroyed = true;
    this.isInitialized = false;
    this.pendingFirstPresenceMs.clear();
    teardownPresenceMetrics();
    this.updateCallbacks.clear();
    this.listFallbackTimers.forEach(clearTimeout);
    this.listFallbackTimers = [];
    this.reenqueueFallbackTimers.forEach(clearTimeout);
    this.reenqueueFallbackTimers = [];
    if (this.noUpdatedWarningTimer) {
      clearTimeout(this.noUpdatedWarningTimer);
      this.noUpdatedWarningTimer = null;
    }
    if (PresenceManager.shared === this) {
      PresenceManager.shared = null;
    }

    const presence = this.webex?.internal?.presence;

    const activeIds = Array.from(this.userRefCount.entries())
      .filter(([, count]) => count > 0)
      .map(([id]) => id);
    if (presence && typeof presence.unsubscribe === 'function' && activeIds.length > 0) {
      void Promise.resolve(presence.unsubscribe(activeIds)).catch((e) => {
        Logger.error(
          `[PresenceManager] unsubscribe on destroy failed: ${e instanceof Error ? e.message : e}`
        );
      });
    }

    if (presence && typeof presence.dequeue === 'function') {
      this.userRefCount.forEach((count, userId) => {
        if (count > 0) {
          try {
            presence.dequeue(userId);
          } catch (e) {
            Logger.error(`[PresenceManager] dequeue on destroy failed for ${userId}: ${e instanceof Error ? e.message : e}`);
          }
        }
      });
    }

    if (presence && typeof presence.off === 'function' && this.updateHandler) {
      try {
        presence.off('updated', this.updateHandler);
      } catch (e) {
        Logger.error(`[PresenceManager] Error removing presence listener: ${e instanceof Error ? e.message : e}`);
      }
      this.updateHandler = null;
    }

    this.userRefCount.clear();
    this.featureSubscriptions.clear();
    Logger.info('[PresenceManager] Destroyed');
  }

  private handleUpdate(envelope: any): void {
    if (this.isDestroyed) {
      return;
    }
    this.lastUpdatedEventTime = Date.now();
    try {
      const envelopeType = envelope?.type;

      if (envelopeType === 'presence') {
        let statusList = envelope?.payload?.statusList;
        if (!Array.isArray(statusList) && Array.isArray(envelope?.payload)) {
          statusList = envelope.payload;
        }
        if (!Array.isArray(statusList) && Array.isArray(envelope?.statusList)) {
          statusList = envelope.statusList;
        }
        if (Array.isArray(statusList)) {
          statusList.forEach((item: any) => {
            const userId = item?.subject || item?.userId || item?.id;
            if (userId) {
              this.updatePresenceData(userId, {
                status: (item.status || 'unknown') as any,
                category: item.category || null,
                customStatusMessage: item.customStatusMessage,
                meetingType: PresenceManager.pickMeetingType(item),
              });
            }
          });
          return;
        }
        Logger.info(
          `[PresenceManager] presence envelope missing statusList; payload keys=${Object.keys(envelope?.payload || {}).join(',')}`
        );
      }

      if (envelopeType === 'subscription') {
        const payload = envelope?.payload;
        const userId = payload?.subject || payload?.userId || payload?.id;
        if (userId) {
          this.updatePresenceData(userId, {
            status: (payload.status || payload.presenceStatus || 'unknown') as any,
            category: payload.category || payload.presenceCategory || null,
            customStatusMessage: payload.customStatusMessage,
            meetingType: PresenceManager.pickMeetingType(payload),
          });
        } else {
          Logger.info(
            `[PresenceManager] subscription envelope missing subject; payload keys=${Object.keys(payload || {}).join(',')}`
          );
        }
        return;
      }

      if (envelopeType === 'delete') {
        const raw = envelope?.payload;
        const userIdsToDelete = Array.isArray(raw) ? raw : [raw];
        userIdsToDelete.forEach((id: string) => {
          if (id) {
            this.presenceMap.delete(id);
          }
        });
        this.scheduleNotify();
        return;
      }

      let presenceData = envelope?.payload || envelope?.data?.payload || envelope?.data || envelope;
      if (presenceData?.payload && presenceData?.eventType) {
        presenceData = presenceData.payload;
      }
      const userId =
        presenceData?.subject ||
        presenceData?.userId ||
        presenceData?.id ||
        envelope?.subject ||
        envelope?.userId ||
        envelope?.id;
      if (userId) {
        this.updatePresenceData(userId, {
          status: (presenceData?.status || envelope?.status || 'unknown') as any,
          category: presenceData?.category || envelope?.category || null,
          customStatusMessage: presenceData?.customStatusMessage || envelope?.customStatusMessage,
          meetingType:
            PresenceManager.pickMeetingType(presenceData) ?? PresenceManager.pickMeetingType(envelope),
        });
        return;
      }

      Logger.info(
        `[PresenceManager] Unhandled presence envelope: type=${envelopeType} keys=${Object.keys(envelope || {}).join(',')}`
      );
      try {
        Logger.info(`[PresenceManager] envelope dump: ${JSON.stringify(envelope)}`);
      } catch {
        /* ignore */
      }
    } catch (error) {
      Logger.error(`[PresenceManager] handleUpdate error: ${error instanceof Error ? error.message : error}`);
    }
  }

  private updatePresenceData(userId: string, data: PresenceData): void {
    const previousData = this.presenceMap.get(userId);
    const statusChanged = !previousData || previousData.status !== data.status;
    const categoryChanged = !previousData || previousData.category !== data.category;
    const messageChanged =
      !previousData || (previousData.customStatusMessage || '') !== (data.customStatusMessage || '');
    const meetingTypeChanged =
      !previousData || (previousData.meetingType || '') !== (data.meetingType || '');
    if (previousData && !statusChanged && !categoryChanged && !messageChanged && !meetingTypeChanged) {
      return;
    }

    const pendingStart = this.pendingFirstPresenceMs.get(userId);
    if (pendingStart !== undefined) {
      this.pendingFirstPresenceMs.delete(userId);
      const latencyMs = Math.round(performance.now() - pendingStart);
      void resolvePresenceSurface().then((surface) => {
        try {
          emitPresenceFirstUpdateLatency(surface, latencyMs);
        } catch (e) {
          Logger.error(
            `[PresenceManager] emitPresenceFirstUpdateLatency failed: ${e instanceof Error ? e.message : String(e)}`
          );
        }
      });
    }

    this.presenceMap.set(userId, data);
    this.scheduleNotify();
    try {
      recordPresenceStatusUpdate(data);
    } catch (e) {
      Logger.error(
        `[PresenceManager] recordPresenceStatusUpdate failed (metrics): ${e instanceof Error ? e.message : String(e)}`
      );
    }
  }

  private scheduleNotify(): void {
    if (this.notifyScheduled) {
      return;
    }
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      const copy = new Map(this.presenceMap);
      this.updateCallbacks.forEach((cb) => {
        try {
          cb(copy);
        } catch (e) {
          Logger.error(`[PresenceManager] Subscriber error: ${e instanceof Error ? e.message : e}`);
        }
      });
    });
  }

  private applyUsersAdded(userIds: string[]): void {
    const presence = this.webex?.internal?.presence;
    if (!presence || typeof presence.enqueue !== 'function') {
      Logger.error('[PresenceManager] presence.enqueue not available');
      return;
    }

    for (const userId of userIds) {
      const next = (this.userRefCount.get(userId) || 0) + 1;
      this.userRefCount.set(userId, next);
      if (next === 1) {
        this.pendingFirstPresenceMs.set(userId, performance.now());
        if (!this.isPersonUuid(userId)) {
          Logger.info(`[PresenceManager] WARN enqueue id is not a person UUID (unexpected): "${userId}"`);
        }
        try {
          presence.enqueue(userId);
        } catch (e) {
          Logger.error(`[PresenceManager] enqueue failed for ${userId}: ${e instanceof Error ? e.message : e}`);
        }
      }
    }
  }

  /** Immediate Mercury subscription; complements `enqueue` which may be delayed by the SDK worker. */
  private async subscribePersonIdsImmediate(userIds: string[]): Promise<void> {
    const presence = this.webex?.internal?.presence;
    if (!userIds.length || !presence || typeof presence.subscribe !== 'function') {
      return;
    }
    try {
      await presence.subscribe(userIds, PresenceManager.SUBSCRIBE_TTL_SEC);
    } catch (e) {
      Logger.error(
        `[PresenceManager] presence.subscribe failed: ${e instanceof Error ? e.message : e}`
      );
    }
  }

  private applyUsersRemoved(userIds: string[]): void {
    const presence = this.webex?.internal?.presence;
    if (!presence || typeof presence.dequeue !== 'function') {
      return;
    }

    const toUnsubscribe: string[] = [];
    for (const userId of userIds) {
      const current = this.userRefCount.get(userId) || 0;
      if (current <= 0) {
        this.userRefCount.delete(userId);
        this.pendingFirstPresenceMs.delete(userId);
        continue;
      }
      const next = current - 1;
      if (next === 0) {
        this.userRefCount.delete(userId);
        this.pendingFirstPresenceMs.delete(userId);
        toUnsubscribe.push(userId);
        try {
          presence.dequeue(userId);
        } catch (e) {
          Logger.error(`[PresenceManager] dequeue failed for ${userId}: ${e instanceof Error ? e.message : e}`);
        }
      } else {
        this.userRefCount.set(userId, next);
      }
    }

    if (toUnsubscribe.length > 0 && typeof presence.unsubscribe === 'function') {
      void Promise.resolve(presence.unsubscribe(toUnsubscribe)).catch((e) => {
        Logger.error(
          `[PresenceManager] presence.unsubscribe failed: ${e instanceof Error ? e.message : e}`
        );
      });
    }
  }

  private validateUserIds(userIds: string[]): string[] {
    return userIds.filter((userId) => {
      if (!userId || typeof userId !== 'string') {
        return false;
      }
      const trimmed = userId.trim();
      if (!trimmed || trimmed === '00000000-0000-0000-0000-000000000000') {
        return false;
      }
      return true;
    });
  }

  private isPersonUuid(s: string): boolean {
    return PresenceManager.PERSON_UUID_RE.test(s.trim());
  }

  private looksLikeEmail(s: string): boolean {
    return /^\S+@\S+\.\S+$/.test(s.trim());
  }

  /** Resolve emails to Webex person UUIDs; pass through valid person UUIDs. */
  private async resolveUserIdForPresence(raw: string): Promise<string | undefined> {
    const t = raw.trim();
    if (!t) {
      return undefined;
    }
    if (this.isPersonUuid(t)) {
      return t;
    }
    if (this.looksLikeEmail(t)) {
      const uuid = await resolveWebexUserId({ email: t.toLowerCase() });
      if (uuid) {
        return uuid;
      }
      Logger.error(`[PresenceManager] people.list could not resolve email: ${t}`);
      return undefined;
    }
    // Opaque directory / Graph / encoded ids — same resolver as speed dial & search lists
    const mapped = await resolveWebexUserId({ contactId: t });
    if (mapped && this.isPersonUuid(mapped)) {
      return mapped;
    }
    return undefined;
  }

  private async resolveUserIdsForPresence(ids: string[]): Promise<string[]> {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const id of ids) {
      const resolved = await this.resolveUserIdForPresence(id);
      if (resolved && !seen.has(resolved)) {
        seen.add(resolved);
        out.push(resolved);
      }
    }
    return out;
  }

  /** Re-call enqueue for every personId still referenced (worker restarts / subscription repair). */
  private reenqueueAllSubscribedPersonIds(_reason: string): void {
    const presence = this.webex?.internal?.presence;
    if (!presence || typeof presence.enqueue !== 'function') {
      return;
    }
    const ids = Array.from(this.userRefCount.entries())
      .filter(([, count]) => count > 0)
      .map(([id]) => id);
    if (ids.length === 0) {
      return;
    }
    for (const id of ids) {
      try {
        presence.enqueue(id);
      } catch (e) {
        Logger.error(`[PresenceManager] re-enqueue failed for ${id}: ${e instanceof Error ? e.message : e}`);
      }
    }
  }

  /** Populate map from REST immediately after enqueue (complements realtime `updated`). */
  private fetchPresenceListBatch(userIds: string[]): void {
    const presence = this.webex?.internal?.presence;
    if (!userIds.length || !presence || typeof presence.list !== 'function') {
      return;
    }
    presence
      .list(userIds)
      .then((results: any) => {
        if (this.isDestroyed) {
          return;
        }
        const list = results?.statusList;
        if (!Array.isArray(list)) {
          return;
        }
        list.forEach((item: any) => {
          if (!item) {
            return;
          }
          const userId = item?.subject || item?.userId || item?.id;
          if (userId) {
            this.updatePresenceData(userId, {
              status: (item.status || 'unknown') as any,
              category: item.category || null,
              customStatusMessage: item.customStatusMessage,
              meetingType: PresenceManager.pickMeetingType(item),
            });
          }
        });
      })
      .catch((err: any) => {
        Logger.error(`[PresenceManager] presence.list() batch failed: ${err instanceof Error ? err.message : err}`);
      });
  }

  /**
   * If no map entry after delay, list again. If no realtime event since this batch, log explicitly.
   */
  private scheduleListFallbackForUsers(userIds: string[], batchStartMs: number): void {
    const delayMs = 3000;
    const timer = setTimeout(() => {
      const idx = this.listFallbackTimers.indexOf(timer);
      if (idx !== -1) {
        this.listFallbackTimers.splice(idx, 1);
      }
      if (this.isDestroyed) {
        return;
      }
      if (this.lastUpdatedEventTime < batchStartMs) {
        Logger.info(
          `[PresenceManager] No presence.on("updated") event observed since this enqueue batch (batchStart=${batchStartMs}); using list fallback / refresh`
        );
      }
      const missing = userIds.filter((id) => !this.presenceMap.has(id));
      if (missing.length === 0) {
        this.fetchPresenceListBatch(userIds);
        return;
      }
      Logger.info(
        `[PresenceManager] list fallback after ${delayMs}ms: ${missing.length} user(s) still without map entry`
      );
      this.fetchPresenceListBatch(missing);
    }, delayMs);
    this.listFallbackTimers.push(timer);
  }

  /** If `updated` never arrives after enqueue, call `enqueue` again for all tracked person IDs (reload repair). */
  private scheduleReenqueueFallback(batchStartMs: number): void {
    const delayMs = 5000;
    const timer = setTimeout(() => {
      const idx = this.reenqueueFallbackTimers.indexOf(timer);
      if (idx !== -1) {
        this.reenqueueFallbackTimers.splice(idx, 1);
      }
      if (this.isDestroyed) {
        return;
      }
      if (this.lastUpdatedEventTime < batchStartMs) {
        Logger.info(
          `[PresenceManager] No presence.on("updated") within ${delayMs}ms after enqueue batch — re-enqueue all personIds`
        );
        this.reenqueueAllSubscribedPersonIds('5s post-enqueue re-subscribe');
      }
    }, delayMs);
    this.reenqueueFallbackTimers.push(timer);
  }

  private cleanupUnusedPresence(): void {
    const toRemove: string[] = [];
    this.presenceMap.forEach((_, userId) => {
      if ((this.userRefCount.get(userId) || 0) === 0) {
        toRemove.push(userId);
      }
    });
    if (toRemove.length === 0) {
      return;
    }
    toRemove.forEach((id) => this.presenceMap.delete(id));
    Logger.info(`[PresenceManager] Cleaned presence entries for ${toRemove.length} unused users`);
    this.scheduleNotify();
  }

  private getAllSubscribedUsers(): Set<string> {
    const out = new Set<string>();
    this.featureSubscriptions.forEach((set) => set.forEach((id) => out.add(id)));
    return out;
  }

  getDebugInfo(): {
    isInitialized: boolean;
    featureCount: number;
    totalSubscribedUsers: number;
    presenceDataCount: number;
    features: string[];
    refCounts: Array<{ userId: string; count: number }>;
  } {
    const all = this.getAllSubscribedUsers();
    return {
      isInitialized: this.isInitialized,
      featureCount: this.featureSubscriptions.size,
      totalSubscribedUsers: all.size,
      presenceDataCount: this.presenceMap.size,
      features: Array.from(this.featureSubscriptions.keys()),
      refCounts: Array.from(this.userRefCount.entries()).map(([userId, count]) => ({ userId, count })),
    };
  }
}
